import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';

import axiosInstance from 'axiosInstance';
import PageHeader from 'components/common/PageHeader';
import { useAuth } from 'context/AuthContext';

const SESSION_TYPE_LABELS = {
  viewer: 'SUPPORT.SESSION_TYPE.VIEWER',
  setup_admin: 'SUPPORT.SESSION_TYPE.ADMIN'
};

const SESSION_TYPE_VARIANTS = {
  viewer: 'info',
  setup_admin: 'warning'
};

const SupportDashboardPage = () => {
  const { t } = useTranslation();
  const { userData, enterSupportSession } = useAuth();
  const navigate = useNavigate();
  const isSupportAdmin = userData?.workspace?.mode === 'support' &&
    userData?.workspace?.platform_role === 'support_admin';

  // Companies search
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [companiesError, setCompaniesError] = useState('');

  // Active session
  const [activeSession, setActiveSession] = useState(null);
  const [loadingSession, setLoadingSession] = useState(true);

  // Recent sessions
  const [recentSessions, setRecentSessions] = useState([]);

  // Start session modal
  const [showStartModal, setShowStartModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [sessionType, setSessionType] = useState('viewer');
  const [sessionReason, setSessionReason] = useState('');
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState('');

  // Ending session
  const [ending, setEnding] = useState(false);

  // Entering (masquerade)
  const [entering, setEntering] = useState(false);

  const loadCompanies = useCallback(async (searchTerm, currentPage) => {
    setLoadingCompanies(true);
    setCompaniesError('');
    try {
      const resp = await axiosInstance.get('login/support/companies', {
        params: { search: searchTerm, page: currentPage, page_size: 20 }
      });
      setCompanies(resp.data.companies || []);
      setTotal(resp.data.total || 0);
    } catch (e) {
      setCompaniesError(e.response?.data?.error || t('SUPPORT.ERROR_LOAD_COMPANIES'));
    } finally {
      setLoadingCompanies(false);
    }
  }, [t]);

  const loadActiveSession = useCallback(async () => {
    setLoadingSession(true);
    try {
      const resp = await axiosInstance.get('login/support/current-session');
      setActiveSession(resp.data.session);
    } catch {
      setActiveSession(null);
    } finally {
      setLoadingSession(false);
    }
  }, []);

  const loadRecentSessions = useCallback(async () => {
    try {
      const resp = await axiosInstance.get('login/support/sessions');
      setRecentSessions(resp.data.sessions || []);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    loadActiveSession();
    loadRecentSessions();
  }, [loadActiveSession, loadRecentSessions]);

  useEffect(() => {
    loadCompanies(search, page);
  }, [loadCompanies, search, page]);

  const handleSearch = e => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const openStartModal = company => {
    setSelectedCompany(company);
    setSessionType('viewer');
    setSessionReason('');
    setStartError('');
    setShowStartModal(true);
  };

  const handleStartSession = async () => {
    if (!sessionReason.trim()) {
      setStartError(t('SUPPORT.REASON_REQUIRED'));
      return;
    }
    setStarting(true);
    setStartError('');
    try {
      const resp = await axiosInstance.post('login/support/sessions', {
        company_id: selectedCompany.id,
        session_type: sessionType,
        reason: sessionReason.trim()
      });
      setActiveSession(resp.data.session);
      setShowStartModal(false);
      loadRecentSessions();
    } catch (e) {
      setStartError(e.response?.data?.error || t('SUPPORT.ERROR_START_SESSION'));
    } finally {
      setStarting(false);
    }
  };

  const handleEndSession = async () => {
    if (!activeSession) return;
    setEnding(true);
    try {
      await axiosInstance.delete(`login/support/sessions/${activeSession.id}`);
      setActiveSession(null);
      loadRecentSessions();
    } catch {
      // ignore
    } finally {
      setEnding(false);
    }
  };

  const handleEnterSession = async () => {
    if (!activeSession) return;
    setEntering(true);
    try {
      await enterSupportSession(activeSession.id);
      navigate('/dashboard');
    } catch (e) {
      // stay on page — context not changed
      console.error('Failed to enter support session', e);
    } finally {
      setEntering(false);
    }
  };

  return (
    <>
      <PageHeader
        title={t('SUPPORT.TITLE')}
        description={t('SUPPORT.DESCRIPTION')}
        className="mb-4"
      />

      {/* Active Session Banner */}
      {!loadingSession && activeSession && (
        <Alert variant="warning" className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <FontAwesomeIcon icon="eye" className="me-2" />
            <strong>{t('SUPPORT.ACTIVE_SESSION')}: </strong>
            {activeSession.company_name}
            {' '}
            <Badge bg={SESSION_TYPE_VARIANTS[activeSession.session_type]}>
              {t(SESSION_TYPE_LABELS[activeSession.session_type] || activeSession.session_type)}
            </Badge>
            <div className="fs--1 text-600 mt-1">
              {t('SUPPORT.REASON')}: {activeSession.reason}
            </div>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="primary"
              size="sm"
              disabled={entering}
              onClick={handleEnterSession}
            >
              {entering
                ? <Spinner size="sm" animation="border" />
                : <><FontAwesomeIcon icon="sign-in-alt" className="me-1" />{t('SUPPORT.ENTER_SESSION')}</>}
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              disabled={ending}
              onClick={handleEndSession}
            >
              {ending
                ? <Spinner size="sm" animation="border" />
                : t('SUPPORT.END_SESSION')}
            </Button>
          </div>
        </Alert>
      )}

      <Row className="g-4">
        {/* Company Picker */}
        <Col lg={8}>
          <Card className="border">
            <Card.Header>
              <h6 className="mb-0">{t('SUPPORT.COMPANIES_TITLE')}</h6>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSearch} className="mb-3">
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder={t('SUPPORT.SEARCH_PLACEHOLDER')}
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                  />
                  <Button type="submit" variant="primary">
                    <FontAwesomeIcon icon="search" />
                  </Button>
                </InputGroup>
              </Form>

              {companiesError ? <Alert variant="danger">{companiesError}</Alert> : null}

              {loadingCompanies ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : companies.length === 0 ? (
                <div className="text-center text-600 py-4">{t('SUPPORT.NO_COMPANIES')}</div>
              ) : (
                <>
                  <Table responsive className="mb-0 fs--1">
                    <thead className="bg-200 text-900">
                      <tr>
                        <th>{t('SUPPORT.COL_COMPANY')}</th>
                        <th>{t('SUPPORT.COL_OWNER')}</th>
                        <th>{t('SUPPORT.COL_PARTNER')}</th>
                        <th className="text-end">{t('SUPPORT.COL_ACTIONS')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {companies.map(company => (
                        <tr key={company.id}>
                          <td className="align-middle fw-semi-bold">{company.name}</td>
                          <td className="align-middle text-600">
                            <div>{company.owner}</div>
                            <div className="fs--2 text-500">{company.owner_email}</div>
                          </td>
                          <td className="align-middle text-600 fs--2">
                            {company.referred_by_partner_code || '-'}
                          </td>
                          <td className="align-middle text-end">
                            <Button
                              size="sm"
                              variant={
                                activeSession?.company_id === company.id
                                  ? 'success'
                                  : 'outline-primary'
                              }
                              onClick={() => openStartModal(company)}
                            >
                              {activeSession?.company_id === company.id
                                ? t('SUPPORT.IN_SESSION')
                                : t('SUPPORT.START_SESSION')}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {total > 20 && (
                    <div className="d-flex justify-content-between align-items-center mt-3 fs--1 text-600">
                      <span>{t('SUPPORT.SHOWING', { count: companies.length, total })}</span>
                      <div>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="me-2"
                          disabled={page === 1}
                          onClick={() => setPage(p => p - 1)}
                        >
                          {t('COMMON.PREV', { defaultValue: '← Anterior' })}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          disabled={page * 20 >= total}
                          onClick={() => setPage(p => p + 1)}
                        >
                          {t('COMMON.NEXT', { defaultValue: 'Siguiente →' })}
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Recent Sessions */}
        <Col lg={4}>
          <Card className="border">
            <Card.Header>
              <h6 className="mb-0">{t('SUPPORT.RECENT_SESSIONS')}</h6>
            </Card.Header>
            <Card.Body className="p-0">
              {recentSessions.length === 0 ? (
                <div className="text-center text-600 py-4 fs--1">
                  {t('SUPPORT.NO_RECENT_SESSIONS')}
                </div>
              ) : (
                <div className="list-group list-group-flush fs--1">
                  {recentSessions.slice(0, 10).map(s => (
                    <div key={s.id} className="list-group-item px-3 py-2">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="fw-semi-bold text-truncate me-2">{s.company_name}</div>
                        <Badge
                          bg={s.is_active ? 'success' : 'secondary'}
                          className="flex-shrink-0"
                        >
                          {s.is_active ? t('SUPPORT.ACTIVE') : t('SUPPORT.ENDED')}
                        </Badge>
                      </div>
                      <div className="text-600 text-truncate">{s.reason}</div>
                      <div className="text-500 fs--2">
                        {new Date(s.started_at).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Start Session Modal */}
      <Modal show={showStartModal} onHide={() => setShowStartModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-0">
            {t('SUPPORT.START_SESSION_MODAL_TITLE', { company: selectedCompany?.name })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {startError ? <Alert variant="danger">{startError}</Alert> : null}

          {activeSession && activeSession.company_id !== selectedCompany?.id && (
            <Alert variant="warning" className="fs--1">
              {t('SUPPORT.WILL_REPLACE_SESSION', { company: activeSession.company_name })}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>{t('SUPPORT.SESSION_TYPE_LABEL')}</Form.Label>
            <Form.Select
              value={sessionType}
              onChange={e => setSessionType(e.target.value)}
            >
              <option value="viewer">{t('SUPPORT.SESSION_TYPE.VIEWER')}</option>
              {isSupportAdmin && (
                <option value="setup_admin">{t('SUPPORT.SESSION_TYPE.ADMIN')}</option>
              )}
            </Form.Select>
            <Form.Text className="text-muted">{t('SUPPORT.SESSION_TYPE_HELP')}</Form.Text>
          </Form.Group>

          <Form.Group>
            <Form.Label>
              {t('SUPPORT.REASON_LABEL')}
              <span className="text-danger ms-1">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={sessionReason}
              onChange={e => setSessionReason(e.target.value)}
              placeholder={t('SUPPORT.REASON_PLACEHOLDER')}
            />
            <Form.Text className="text-muted">{t('SUPPORT.REASON_HELP')}</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStartModal(false)} disabled={starting}>
            {t('COMMON.CANCEL', { defaultValue: 'Cancelar' })}
          </Button>
          <Button variant="primary" onClick={handleStartSession} disabled={starting}>
            {starting
              ? <Spinner size="sm" animation="border" />
              : t('SUPPORT.CONFIRM_START')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SupportDashboardPage;
